import { Directive, HostListener, ElementRef, Renderer2, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommaSeparatorService } from '../service/comma-separator.service';

@Directive({
    selector: '[appNumberWithCommaSeparator]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumberWithCommaSeparatorDirective),
            multi: true
        }
    ],
    standalone: false
})
export class NumberWithCommaSeparatorDirective implements ControlValueAccessor {
  private onChange = (value: any) => {};
  private onTouched = () => {};

  @Input() allowDecimals: boolean = false; // For input fields that allow decimals
  @Input() maxDecimalPlaces: number = 4; // For input fields that allow decimals, max decimal places
  @Input() allowNegative: boolean = false; // For input fields that allow negative numbers
  @Input() allowComma: boolean = true; // For controlling comma separator display
  @Input() selectEndOnFocus = false;
  @Input() maxIntegerDigits: number = this.commaSeparatorService.MAX_SAFE_INTEGER_DIGITS;

  private lastValue: number | null = null;
  private _bracketNegative = false;

  @Input()
  set bracketNegative(value: boolean) {
    this._bracketNegative = value === true || value === ('' as any);
    this.refreshDisplay();
  }

  get bracketNegative(): boolean {
    return this._bracketNegative;
  }

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private commaSeparatorService: CommaSeparatorService
  ) {}

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = this.sanitizeRawValue(input.value ?? '');
    this.applyValueToInput(input, value);
  }

  private sanitizeRawValue(value: string): string {
    if (this.bracketNegative) {
      value = value.replace(/[()]/g, '');
    }

    if (this.allowDecimals) {
      value = this.allowNegative ? value.replace(/[^0-9.-]/g, '') : value.replace(/[^0-9.]/g, '');
    } else {
      value = this.allowNegative ? value.replace(/[^0-9-]/g, '') : value.replace(/[^0-9]/g, '');
    }

    if (this.allowNegative && value.includes('-')) {
      const minusCount = (value.match(/-/g) || []).length;
      if (minusCount > 1 || (value.includes('-') && !value.startsWith('-'))) {
        value = '-' + value.replace(/-/g, '');
      }
    }

    if (this.allowDecimals && value.includes('.')) {
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      const [integerPart, decimalPart = ''] = value.split('.');
      value = integerPart + '.' + decimalPart.substring(0, this.maxDecimalPlaces);
    }

    return this.commaSeparatorService.limitDigitLength(value, this.maxIntegerDigits);
  }

  private applyValueToInput(input: HTMLInputElement, value: string): void {
    const isEffectivelyEmpty = value === '' || value === '-' || value === '.';
    const displayValue = this.toDisplayValue(value, isEffectivelyEmpty);

    this.renderer.setProperty(input, 'value', displayValue);

    if (isEffectivelyEmpty) {
      this.lastValue = null;
      this.onChange(null);
      return;
    }

    const numericValue = this.allowDecimals ? parseFloat(value) : parseInt(value, 10);
    const parsedValue = isNaN(numericValue) ? null : numericValue;
    this.lastValue = parsedValue;
    this.onChange(parsedValue);
  }

  @HostListener('blur')
  onBlur() {
    this.onTouched();
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    if (!this.selectEndOnFocus) {
      return;
    }

    const input = this.el.nativeElement as HTMLInputElement;
    if (input.disabled) {
      return;
    }

    if (document.activeElement !== input) {
      event.preventDefault();
      input.focus();
      this.moveCursorToEnd();
    }
  }

  @HostListener('focus')
  onFocus(): void {
    if (!this.selectEndOnFocus) {
      return;
    }

    this.moveCursorToEnd();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;

    if (this.bracketNegative && (event.key === 'Backspace' || event.key === 'Delete')) {
      if (this.handleBracketNegativeDeletion(event, input)) {
        return;
      }
    }

    const allowedKeys = [
      'Backspace', 'Delete', 'Escape', 'Enter', 'Home', 'End',
      'ArrowLeft', 'ArrowRight', 'Tab', 'a', 'c', 'v', 'x'
    ];

    // Allow control keys
    if (allowedKeys.includes(event.key) ||
        (['a', 'c', 'v', 'x'].includes(event.key) && (event.ctrlKey || event.metaKey))) {
      return;
    }

    // Allow minus sign if negative numbers are allowed
    if (event.key === '-' && this.allowNegative) {
      const cursorPosition = input.selectionStart || 0;
      
      // Only allow minus at the beginning
      if (cursorPosition !== 0 || input.value.includes('-')) {
        event.preventDefault();
      }
      return;
    }

    // Allow decimal point if decimals are allowed
    if (event.key === '.' && this.allowDecimals) {
      if (input.value.includes('.')) {
        event.preventDefault();
      }
      return;
    }

    // Block parentheses when they are auto-applied around the value
    if (this.bracketNegative && (event.key === '(' || event.key === ')')) {
      event.preventDefault();
      return;
    }

    // Only allow numbers
    if (!isNaN(Number(event.key))) {
      if (!this.canAcceptDigitAtCursor(input)) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
  }

  private mapDisplayIndexToRawIndex(display: string, index: number): number {
    const innerStart = this.bracketNegative && display.startsWith('(') ? 1 : 0;
    const innerEnd = this.bracketNegative && display.endsWith(')') ? display.length - 1 : display.length;
    const clampedIndex = Math.max(innerStart, Math.min(index, innerEnd));
    let rawIndex = 0;

    for (let displayIndex = innerStart; displayIndex < clampedIndex; displayIndex++) {
      const char = display[displayIndex];
      if (char !== ',') {
        rawIndex++;
      }
    }

    return rawIndex;
  }

  private countDigits(value: string): number {
    return value.replace(/[^0-9]/g, '').length;
  }

  private canAcceptDigitAtCursor(input: HTMLInputElement): boolean {
    const display = input.value ?? '';
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;
    const raw = this.getRawNumericString(display);
    const rawStart = this.mapDisplayIndexToRawIndex(display, selectionStart);
    const rawEnd = this.mapDisplayIndexToRawIndex(display, selectionEnd);
    const dotIndex = raw.indexOf('.');

    if (this.allowDecimals && dotIndex >= 0 && rawStart > dotIndex) {
      const decimalPart = raw.slice(dotIndex + 1);
      const selectedDecimalDigits = this.countDigits(raw.slice(Math.max(dotIndex + 1, rawStart), rawEnd));
      return (this.countDigits(decimalPart) - selectedDecimalDigits + 1) <= this.maxDecimalPlaces;
    }

    const integerEnd = dotIndex >= 0 ? dotIndex : raw.length;
    const integerPart = raw.slice(0, integerEnd);
    const selectedIntegerDigits = this.countDigits(raw.slice(rawStart, Math.min(rawEnd, integerEnd)));
    return (this.countDigits(integerPart) - selectedIntegerDigits + 1) <= this.maxIntegerDigits;
  }

  private formatWithIndianCommas(value: string): string {
    if (!value || value === '') return '';
    
    // Handle negative numbers
    let isNegative = false;
    if (this.allowNegative && value.startsWith('-')) {
      isNegative = true;
      value = value.substring(1); // Remove the minus sign temporarily
    }
    
    // Remove any existing commas
    const cleanValue = this.commaSeparatorService.limitDigitLength(value.replace(/,/g, ''), this.maxIntegerDigits);
    
    // Handle decimal numbers
    if (this.allowDecimals && cleanValue.includes('.')) {
      const parts = cleanValue.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];
      
      const formattedInteger = this.commaSeparatorService.currencySeparatorBD(integerPart);
      const result = formattedInteger + '.' + decimalPart;
      return isNegative ? '-' + result : result;
    } else {
      const result = this.commaSeparatorService.currencySeparatorBD(cleanValue);
      return isNegative ? '-' + result : result;
    }
  }

  private handleBracketNegativeDeletion(event: KeyboardEvent, input: HTMLInputElement): boolean {
    const display = input.value;
    if (!display.startsWith('(') || !display.endsWith(')')) {
      return false;
    }

    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;
    const innerStart = 1;
    const innerEnd = display.length - 1;
    const raw = this.getRawNumericString(display);

    if (!raw) {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
      }
      return true;
    }

    if (selectionStart !== selectionEnd) {
      event.preventDefault();
      const { rawStart, rawEnd } = this.mapDisplayRangeToRawRange(display, selectionStart, selectionEnd);
      this.applyRawValue(input, raw.slice(0, rawStart) + raw.slice(rawEnd), 'end');
      return true;
    }

    if (event.key === 'Backspace') {
      if (selectionStart <= innerStart) {
        event.preventDefault();
        const deleteIndex = selectionStart === 0 ? raw.length - 1 : 0;
        const cursor = selectionStart === 0 ? 'end' : 'start';
        this.applyRawValue(
          input,
          raw.slice(0, deleteIndex) + raw.slice(deleteIndex + 1),
          cursor
        );
        return true;
      }

      if (selectionStart >= innerEnd) {
        event.preventDefault();
        this.applyRawValue(input, raw.slice(0, -1), 'end');
        return true;
      }

      return false;
    }

    if (event.key === 'Delete') {
      if (selectionStart >= innerEnd) {
        event.preventDefault();
        return true;
      }

      if (selectionStart < innerStart) {
        event.preventDefault();
        this.applyRawValue(input, raw.slice(1), 'start');
        return true;
      }

      return false;
    }

    return false;
  }

  private getRawNumericString(display: string): string {
    let value = display.replace(/[()]/g, '').replace(/,/g, '');

    if (this.allowDecimals) {
      value = this.allowNegative ? value.replace(/[^0-9.-]/g, '') : value.replace(/[^0-9.]/g, '');
    } else {
      value = this.allowNegative ? value.replace(/[^0-9-]/g, '') : value.replace(/[^0-9]/g, '');
    }

    return value;
  }

  private mapDisplayRangeToRawRange(display: string, start: number, end: number): { rawStart: number; rawEnd: number } {
    const innerStart = 1;
    const innerEnd = display.length - 1;
    const clampedStart = Math.max(innerStart, Math.min(start, innerEnd));
    const clampedEnd = Math.max(innerStart, Math.min(end, innerEnd));

    const rawStart = display.slice(innerStart, clampedStart).replace(/,/g, '').length;
    const rawEnd = display.slice(innerStart, clampedEnd).replace(/,/g, '').length;

    return { rawStart, rawEnd };
  }

  private applyRawValue(
    input: HTMLInputElement,
    raw: string,
    cursor: 'start' | 'end' = 'end'
  ): void {
    raw = this.sanitizeRawValue(raw);
    const isEffectivelyEmpty = raw === '' || raw === '-' || raw === '.';
    const displayValue = this.toDisplayValue(raw, isEffectivelyEmpty);

    this.renderer.setProperty(input, 'value', displayValue);

    if (isEffectivelyEmpty) {
      this.lastValue = null;
      this.onChange(null);
      this.setCursor(input, 0);
      return;
    }

    const numericValue = this.allowDecimals ? parseFloat(raw) : parseInt(raw, 10);
    const parsedValue = isNaN(numericValue) ? null : numericValue;
    this.lastValue = parsedValue;
    this.onChange(parsedValue);

    const innerEnd = displayValue.length - 1;
    this.setCursor(input, cursor === 'start' ? (this.bracketNegative ? 1 : 0) : (this.bracketNegative ? innerEnd : displayValue.length));
  }

  private setCursor(input: HTMLInputElement, position: number): void {
    input.setSelectionRange(position, position);
  }

  private moveCursorToEnd(): void {
    const input = this.el.nativeElement as HTMLInputElement;

    const placeCursor = (): void => {
      if (document.activeElement !== input) {
        return;
      }

      this.setCursor(input, this.getEndCursorPosition(input));
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        placeCursor();
        setTimeout(placeCursor, 0);
      });
    });
  }

  private getEndCursorPosition(input: HTMLInputElement): number {
    const value = input.value ?? '';

    if (this.bracketNegative && value.startsWith('(') && value.endsWith(')')) {
      return Math.max(1, value.length - 1);
    }

    return value.length;
  }

  private toDisplayValue(value: string, isEffectivelyEmpty: boolean): string {
    if (isEffectivelyEmpty) {
      return '';
    }

    const formattedValue = this.allowComma ? this.formatWithIndianCommas(value) : value;
    return this.bracketNegative ? `(${formattedValue})` : formattedValue;
  }

  private refreshDisplay(): void {
    if (this.lastValue === null || this.lastValue === undefined) {
      this.renderer.setProperty(this.el.nativeElement, 'value', '');
      return;
    }

    const rawValue = this.sanitizeRawValue(this.lastValue.toString());
    this.renderer.setProperty(
      this.el.nativeElement,
      'value',
      this.toDisplayValue(rawValue, false)
    );
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value !== null && value !== undefined) {
      this.lastValue = value;
      const rawValue = this.sanitizeRawValue(value.toString());
      this.renderer.setProperty(
        this.el.nativeElement,
        'value',
        this.toDisplayValue(rawValue, false)
      );
    } else {
      this.lastValue = null;
      this.renderer.setProperty(this.el.nativeElement, 'value', '');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }
}
